import { z } from "zod";
import { PeriodInput, SEARCH_KINDS, SEARCH_LIMITS, SearchHit, SearchKind, UnitPriceHistory } from "../search.js";
import { DOC_QUERY_KINDS, DOC_STATES } from "../searchTerms.js";
import { defineTool } from "./define.js";

/**
 * 横断検索のツール（#224、docs/90 §2.2）。どれも読むだけ（mutating: false）。実装は apps/api/src/tools/search.ts。
 * 段1 は製品の検索窓の社内 LLM（mai-11）が使う。段2 で MCP に同じ定義を出す（docs/87 §8.5）。
 * 計算（足す・割る・率）は DB と handler がする。LLM は質問を読んで、どのツールをどの条件で呼ぶかを決めるだけ
 */

const Kinds = z.array(SearchKind).max(SEARCH_KINDS.length);

// ---------------------------------------------------------------------------
// search.all
// ---------------------------------------------------------------------------
export const SearchAllInput = PeriodInput.extend({
  query: z.string().trim().min(1).max(SEARCH_LIMITS.qMax).describe("探す言葉（会社名・案件名・件名・番号・品名など）。空白で区切ると全部を含むものだけ"),
  kinds: Kinds.optional().describe("種類で絞る。partner＝取引先、project_group＝案件、estimate＝見積書、line＝明細、invoice＝請求書、receipt＝領収書、vendor_quote＝外注見積、rfq＝見積依頼書、received_document＝受け取った書類、delivery＝送付状"),
  status: z.array(z.string().max(20)).max(10).optional().describe("状態で絞るときだけ（draft＝下書き・issued＝未受注・ordered＝受注・lost＝失注・voided＝取消）。絞らないときは付けない"),
});
export type SearchAllInput = z.infer<typeof SearchAllInput>;
export const SearchAllOutput = z.object({
  period: z.object({ from: z.string().nullable(), to: z.string().nullable() }),
  groups: z.array(z.object({ kind: SearchKind, total: z.number().int(), hits: z.array(SearchHit) })),
});
export type SearchAllOutput = z.infer<typeof SearchAllOutput>;
export const searchAllTool = defineTool({
  name: "search.all",
  title: "横断検索",
  description: "言葉で取引先・案件・見積書・明細・請求書・領収書・外注見積・見積依頼書・受け取った書類・送付状をまとめて探すときに使う。種類・期間・状態で絞れる",
  input: SearchAllInput,
  output: SearchAllOutput,
  scope: ["partners:read", "projects:read", "estimates:read", "invoices:read", "vendor_quotes:read", "documents:read"],
  mutating: false,
});

// ---------------------------------------------------------------------------
// lines.unit_price_history
// ---------------------------------------------------------------------------
export const UnitPriceHistoryInput = PeriodInput.extend({
  name: z.string().trim().min(1).max(SEARCH_LIMITS.qMax).describe("明細の名称（品名）。一部でもよい"),
});
export type UnitPriceHistoryInput = z.infer<typeof UnitPriceHistoryInput>;
export const unitPriceHistoryTool = defineTool({
  name: "lines.unit_price_history",
  title: "単価履歴",
  description: "明細の名称（品名）の過去の単価・数量・単位・仕入れ・日付・取引先を新しい順に調べるときに使う。最小・最大・中央値も返す",
  input: UnitPriceHistoryInput,
  output: UnitPriceHistory,
  scope: ["estimates:read"],
  mutating: false,
});

// ---------------------------------------------------------------------------
// amounts.sum
// ---------------------------------------------------------------------------
export const AMOUNT_SUM_KINDS = ["estimate", "invoice", "receipt", "project_group"] as const;
export const AMOUNT_GROUP_BY = ["none", "partner", "project_group", "month", "status"] as const;
export const AMOUNT_SORTS = ["amount_desc", "amount_asc", "rate_asc", "date_desc"] as const;
/** 金額（円）。質問の書き方のまま（「100万円」）でもよい＝API が parseYen で円に直す（AI に換算させない） */
const YenLike = z.union([z.number().min(0), z.string().max(30)]);
const PartnerName = z.string().max(60).describe("取引先の名前で絞るときだけ（「宛て」「様」「株式会社」は付けない）");
const DocState = z.enum(DOC_STATES).describe(
  "状態で絞るときだけ。issued＝発行済（下書き・取消を除く全部。既定）・draft＝下書き・waiting＝未受注（発行済で受注・失注になっていない見積書）・ordered＝受注・lost＝失注・expired＝期限切れ（未受注で有効期限が今日より前）・unpaid＝未入金（請求書の残高あり）・overdue＝期限超過（未入金でお支払期限が過ぎた）・paid＝入金済・voided＝取消",
);
export const AmountsSumInput = PeriodInput.extend({
  kind: z.enum(AMOUNT_SUM_KINDS).describe("estimate＝見積書（発行日。受注・失注は受注日）、invoice＝請求書（請求日）、receipt＝領収書（領収日）、project_group＝受注した案件（受注日。粗利・案件の金額はこれ）"),
  metric: z.enum(["total", "margin"]).default("total").describe("total＝金額の合計と件数、margin＝案件ごとの粗利と粗利率（kind が project_group のとき）"),
  groupBy: z.enum(AMOUNT_GROUP_BY).default("none").describe("まとめ方。none＝全体、partner＝取引先ごと、project_group＝案件ごと、month＝月ごと、status＝状態ごと"),
  state: DocState.optional(),
  partner: PartnerName.optional(),
  marginBelowPct: z.number().min(0).max(100).optional().describe("粗利率がこの％未満の案件だけ（metric が margin のとき）"),
  sort: z.enum(AMOUNT_SORTS).optional().describe("行の並べ方。amount_desc＝金額の大きい順（「一番大きい」）・rate_asc＝粗利率の低い順"),
});
export type AmountsSumInput = z.infer<typeof AmountsSumInput>;
export const AmountRow = z.object({
  key: z.string(),
  label: z.string(),
  url: z.string().nullable(),
  kind: SearchKind.nullable(),
  id: z.string().nullable(),
  count: z.number().int(),
  /** 税込（total）。margin では無い */
  total: z.number().int().nullable(),
  /** 税抜 */
  exTax: z.number().int().nullable(),
  /** margin：仕入れ・粗利・粗利率（％、0.1 刻み）。仕入れ未入力の案件は null */
  cost: z.number().int().nullable(),
  profit: z.number().int().nullable(),
  rate: z.number().nullable(),
});
export type AmountRow = z.infer<typeof AmountRow>;
export const AmountsSumOutput = z.object({
  period: z.object({ from: z.string().nullable(), to: z.string().nullable() }),
  metric: z.enum(["total", "margin"]),
  rows: z.array(AmountRow),
  /** 全体（行の和。DB が数えた値を足しただけ） */
  /** 粗利率（全体）は margin のとき、入れた行の粗利の和 ÷ 税抜の和（％、0.1 刻み。ホームの今月の数字と同じ式） */
  sum: z.object({ count: z.number().int(), total: z.number().int().nullable(), exTax: z.number().int().nullable(), profit: z.number().int().nullable(), rate: z.number().nullable() }),
  /** 入れなかった行（仕入れ未入力の案件など。ルール7） */
  excluded: z.object({ count: z.number().int(), reason: z.string(), rows: z.array(AmountRow) }),
  /** 行が多くて切った（上位 50 まで） */
  truncated: z.boolean(),
});
export type AmountsSumOutput = z.infer<typeof AmountsSumOutput>;
export const amountsSumTool = defineTool({
  name: "amounts.sum",
  title: "金額の集計",
  description: "見積書・請求書・領収書の金額の合計と件数、受注した案件の受注額・粗利と粗利率を、期間・状態・取引先・まとめ方（取引先・案件・月・状態）を決めて調べるときに使う",
  input: AmountsSumInput,
  output: AmountsSumOutput,
  scope: ["estimates:read", "invoices:read", "projects:read"],
  mutating: false,
});

// ---------------------------------------------------------------------------
// amounts.find
// ---------------------------------------------------------------------------
export const AmountsFindInput = z.object({
  amount: z.number().int().min(0).max(10_000_000_000).describe("金額（円）"),
  basis: z.enum(["total", "ex_tax"]).default("total").describe("total＝税込（明細は金額）、ex_tax＝税抜"),
  tolerance: z.number().int().min(0).max(10_000_000).default(0).describe("幅（円）。0 はぴったり"),
  kinds: Kinds.optional().describe("種類で絞る（estimate・invoice・receipt・vendor_quote・received_document・line）"),
});
export type AmountsFindInput = z.infer<typeof AmountsFindInput>;
export const AmountsFindOutput = z.object({ hits: z.array(SearchHit), truncated: z.boolean() });
export type AmountsFindOutput = z.infer<typeof AmountsFindOutput>;
export const amountsFindTool = defineTool({
  name: "amounts.find",
  title: "金額で探す",
  description: "金額（税込・税抜・幅）から見積書・請求書・領収書・外注見積・受け取った書類・明細を探すときに使う",
  input: AmountsFindInput,
  output: AmountsFindOutput,
  scope: ["estimates:read", "invoices:read", "vendor_quotes:read", "documents:read"],
  mutating: false,
});

// ---------------------------------------------------------------------------
// documents.by_status（状態で探す。#224 の手直し、docs/90 §13.6）
// ---------------------------------------------------------------------------
export const DocumentsByStatusInput = PeriodInput.extend({
  kind: z.enum(DOC_QUERY_KINDS).describe("estimate＝見積書、invoice＝請求書、receipt＝領収書"),
  state: DocState.optional(),
  partner: PartnerName.optional(),
  minAmount: YenLike.optional().describe("税込でこの金額以上（質問の書き方のまま「100万円」でよい）"),
  maxAmount: YenLike.optional().describe("税込でこの金額以下"),
  sort: z.enum(["date_desc", "amount_desc", "amount_asc"]).optional().describe("並べ方。既定は新しい順。amount_desc＝金額の大きい順"),
});
export type DocumentsByStatusInput = z.infer<typeof DocumentsByStatusInput>;
/** 1行（索引の行＋状態で探すときの値）。balance＝残高（請求書）・validUntil＝有効期限（見積書）・dueDate＝お支払期限（請求書） */
export const DocumentRow = SearchHit.extend({ balance: z.number().int().nullable(), validUntil: z.string().nullable(), dueDate: z.string().nullable() });
export type DocumentRow = z.infer<typeof DocumentRow>;
export const DocumentsByStatusOutput = z.object({
  period: z.object({ from: z.string().nullable(), to: z.string().nullable() }),
  count: z.number().int(),
  /** 税込の合計（未入金・期限超過は残高の合計） */
  total: z.number().int(),
  basis: z.enum(["total", "balance"]),
  /** 判定できずに入れなかった行（期限切れ＝有効期限を日付で入れていない未受注の見積書） */
  excluded: z.object({ count: z.number().int(), reason: z.string() }),
  rows: z.array(DocumentRow),
  truncated: z.boolean(),
  /** 取引先で絞ったとき、当たった取引先の名前 */
  partners: z.array(z.string()).nullable(),
});
export type DocumentsByStatusOutput = z.infer<typeof DocumentsByStatusOutput>;
export const documentsByStatusTool = defineTool({
  name: "documents.by_status",
  title: "状態で探す",
  description: "見積書・請求書・領収書を、状態（未受注・受注・期限切れ・未入金など）・期間・取引先・金額の範囲で一覧にして件数と合計を調べるときに使う（「未受注の見積は？」「未入金はいくら？」「100万円以上の見積は？」）",
  input: DocumentsByStatusInput,
  output: DocumentsByStatusOutput,
  scope: ["estimates:read", "invoices:read"],
  mutating: false,
});

// ---------------------------------------------------------------------------
// amounts.compare（期間の比較）
// ---------------------------------------------------------------------------
export const AmountsCompareInput = z.object({
  kind: z.enum(AMOUNT_SUM_KINDS).describe("estimate＝見積書・invoice＝請求書・receipt＝領収書・project_group＝受注した案件"),
  metric: z.enum(["total", "margin"]).default("total").describe("total＝金額の合計と件数、margin＝案件の粗利"),
  state: DocState.optional(),
  partner: PartnerName.optional(),
  periodA: PeriodInput.describe("新しい方の期間（例：今月＝{period:this_month}）"),
  periodB: PeriodInput.describe("くらべる方の期間（例：先月＝{period:last_month}）"),
});
export type AmountsCompareInput = z.infer<typeof AmountsCompareInput>;
const CompareSide = z.object({
  period: z.object({ from: z.string().nullable(), to: z.string().nullable() }),
  count: z.number().int(),
  total: z.number().int().nullable(),
  exTax: z.number().int().nullable(),
  profit: z.number().int().nullable(),
});
export const AmountsCompareOutput = z.object({
  a: CompareSide,
  b: CompareSide,
  /** a − b（API が計算） */
  diff: z.object({ count: z.number().int(), total: z.number().int().nullable(), exTax: z.number().int().nullable(), profit: z.number().int().nullable() }),
  /** 増減の率（％、0.1 刻み。b が 0 なら null） */
  changePct: z.number().nullable(),
});
export type AmountsCompareOutput = z.infer<typeof AmountsCompareOutput>;
export const amountsCompareTool = defineTool({
  name: "amounts.compare",
  title: "期間の比較",
  description: "2つの期間（今月と先月・今年と昨年など）の金額の合計・件数・粗利をくらべて、差と増減の率を調べるときに使う",
  input: AmountsCompareInput,
  output: AmountsCompareOutput,
  scope: ["estimates:read", "invoices:read", "projects:read"],
  mutating: false,
});

/** 検索窓の社内 LLM に渡すツール（段1＋#224 の手直しで 状態で探す・期間の比較） */
export const SEARCH_TOOLS = [searchAllTool, unitPriceHistoryTool, amountsSumTool, amountsFindTool, documentsByStatusTool, amountsCompareTool] as const;
